<template>
  <div>
    <v-card class="main-card mx-auto my-10">
      <v-card-title :class="maintenanceMode ? 'color: red' : ''">
        <div :class="maintenanceMode ? 'white--text' : ''">Admin panel</div>
        <v-avatar
          v-if="maintenanceMode"
          slot="icon"
          color="white accent-4"
          size="40"
          class="ml-5"
        >
          <v-icon icon="mdi-lock" color="red"> mdi-lock </v-icon>
        </v-avatar>
        <div v-if="maintenanceMode" class="ml-2 white--text">
          [Maintenance mode]
        </div>
      </v-card-title>
      <v-card-text>
        <v-expansion-panels>
          <v-expansion-panel>
            <v-expansion-panel-header>
              <div class="d-flex flex-row">
                <v-icon
                  color="red darken-2"
                  class="mr-2"
                  v-if="
                    reviewersNeedingVerification.length > 0 ||
                    reviewersNeedingVerificationError
                  "
                >
                  mdi-information-slab-circle-outline
                </v-icon>
                <div class="mt-1">Reviewers needing verification:</div>
                <span
                  :class="
                    reviewersNeedingVerification.length > 0 ? 'red--text' : ''
                  "
                  class="ml-1 mr-1 mt-1"
                  v-if="!reviewersNeedingVerificationLoading"
                >
                  {{ reviewersNeedingVerification.length }}</span
                ><v-progress-circular
                  indeterminate
                  :size="20"
                  color="primary"
                  class="ml-1"
                  v-if="reviewersNeedingVerificationLoading"
                ></v-progress-circular>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-alert
                v-if="reviewersNeedingVerificationError"
                type="error"
                dismissible
              >
                {{ reviewersNeedingVerificationError }}
              </v-alert>
              <v-expansion-panels>
                <v-expansion-panel
                  v-for="(rev, i) in reviewersNeedingVerification"
                  :key="i"
                >
                  <v-expansion-panel-header>
                    {{ rev.Email }}
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <ReviewerVerification :reviewer="rev">
                    </ReviewerVerification>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
        <v-divider class="my-3"></v-divider>
        <v-tabs fixed-tabs show-arrows v-model="activeTab">
          <v-tabs-slider></v-tabs-slider>
          <v-tab @change="enteredMainTab"> Main </v-tab>
          <v-tab-item eager>
            <AdminPanelMainTab
              ref="mainTab"
              @notify="showSnackbar"
            ></AdminPanelMainTab>
          </v-tab-item>
          <v-tab> Stats </v-tab>
          <v-tab-item>
            <v-container>
              <AdminPanelStats
                :initial-date="$route.query.date"
                :initial-mode="$route.query.mode"
                @notify="showSnackbar"
              ></AdminPanelStats>
            </v-container>
          </v-tab-item>
          <v-tab @change="enteredLogsTab"> Logs </v-tab>
          <v-tab-item eager>
            <AdminPanelLogsTab ref="logsTab"></AdminPanelLogsTab>
          </v-tab-item>
          <v-tab @change="getUsers"> Users </v-tab>
          <v-tab-item eager>
            <AdminPanelUsersTab ref="usersTab"></AdminPanelUsersTab>
          </v-tab-item>
          <v-tab @change="getReviewers"> Reviewers </v-tab>
          <v-tab-item eager>
            <AdminPanelReviewersTab
              ref="reviewersTab"
            ></AdminPanelReviewersTab>
          </v-tab-item>
          <v-tab> Settings </v-tab>
          <v-tab-item>
            <AdminPanelSettingsTab
              :maintenance-mode.sync="maintenanceMode"
              :server-started-ts="serverStartedTs"
            ></AdminPanelSettingsTab>
          </v-tab-item>
        </v-tabs>
        <v-snackbar v-model="snackbar" :color="snackbarColor" outlined>
          {{ snackbarText }}
          <template v-slot:action="{ attrs }">
            <v-btn color="black" text v-bind="attrs" @click="snackbar = false">
              Close
            </v-btn>
          </template>
        </v-snackbar>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import ReviewerVerification from "../components/ReviewerVerification.vue";
import AdminPanelStats from "@/components/AdminPanelStats.vue";
import AdminPanelMainTab from "@/components/AdminPanelMainTab.vue";
import AdminPanelLogsTab from "@/components/AdminPanelLogsTab.vue";
import AdminPanelUsersTab from "@/components/AdminPanelUsersTab.vue";
import AdminPanelReviewersTab from "@/components/AdminPanelReviewersTab.vue";
import AdminPanelSettingsTab from "@/components/AdminPanelSettingsTab.vue";

export default {
  components: {
    ReviewerVerification,
    AdminPanelStats,
    AdminPanelMainTab,
    AdminPanelLogsTab,
    AdminPanelUsersTab,
    AdminPanelReviewersTab,
    AdminPanelSettingsTab,
  },
  data() {
    const tabNames = ["main", "stats", "logs", "users", "reviewers", "settings"];
    const queryTab = (this.$route.query.tab || "").toString().toLowerCase();
    const tabIdx = tabNames.indexOf(queryTab);
    return {
      tabNames,
      activeTab: tabIdx !== -1 ? tabIdx : 0,

      reviewersNeedingVerification: [],
      reviewersNeedingVerificationError: "",
      reviewersNeedingVerificationLoading: true,

      snackbar: false,
      snackbarColor: "grey darken-4",
      snackbarText: "",

      maintenanceMode: false,
      serverStartedTs: 0,
    };
  },
  mounted() {
    this.$utils.visit(this);
    this.getBackendStatus();
    this.getReviewersNeedingVerification();
    // Tabs other than "Main" only load their data via their @change handler,
    // which doesn't fire for a tab that's already active on initial render -
    // trigger it manually so deep-linking via ?tab= works.
    this.$nextTick(() => {
      if (this.tabNames[this.activeTab] === "logs") {
        this.$refs.logsTab.enteredLogsTab();
      } else if (this.tabNames[this.activeTab] === "users") {
        this.$refs.usersTab.getUsers();
      } else if (this.tabNames[this.activeTab] === "reviewers") {
        this.$refs.reviewersTab.getReviewers();
      }
    });
  },
  watch: {
    // Keep the URL in sync so the current tab can be bookmarked/shared.
    activeTab(newIdx) {
      const tabName = this.tabNames[newIdx];
      const query = { ...this.$route.query };
      if (tabName === "main") {
        delete query.tab;
      } else {
        query.tab = tabName;
      }
      this.$router.replace({ query }).catch(() => {});
    },
  },
  methods: {
    // Delegate tab-activation events to the active tab's own component.
    enteredMainTab() {
      this.$refs.mainTab.enteredMainTab();
    },
    enteredLogsTab() {
      this.$refs.logsTab.enteredLogsTab();
    },
    getUsers() {
      this.$refs.usersTab.getUsers();
    },
    getReviewers() {
      this.$refs.reviewersTab.getReviewers();
    },
    showSnackbar(text, isError) {
      this.snackbarText = text;
      this.snackbarColor = isError ? "error" : "success";
      this.snackbar = true;
    },
    getBackendStatus() {
      if (!this.$store.getters.isAdmin) {
        return;
      }
      this.$http
        .post(
          this.$consts.BE_ADDRESS + "/admin/getBackendStatus",
          {
            dusi: this.$store.getters.getDusi,
          },
          {
            headers: {
              Authorization: this.$store.state.token,
            },
          },
        )
        .then((response) => {
          if (response.status === 200) {
            console.log("burny! got status: " + JSON.stringify(response.data));
            this.maintenanceMode = response.data.MaintenanceMode;
            this.serverStartedTs = response.data.ServerStaredTs;
            return;
          }
          this.$utils.handleKnownRespCodes(this, response.status);
        })
        .catch((error) => {
          this.$utils.handleBackendError(this, error);
        });
    },
    async getReviewersNeedingVerification() {
      this.reviewersNeedingVerificationLoading = true;
      try {
        this.reviewersNeedingVerificationError = "";
        var resp = await this.$utils.callBackendPost(
          this,
          "/admin/getReviewersNeedingVerification",
          {},
        );
        this.reviewersNeedingVerification = resp;
      } catch (error) {
        this.reviewersNeedingVerificationError = error;
      } finally {
        this.reviewersNeedingVerificationLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.main-card {
  width: 100vw;
  max-width: 1200px;
}
</style>
